import { uniqBy } from 'lodash-es';
import { useSessionContext } from '@/contexts/session/context';
import { useChatContext } from '@/contexts/chat/context';
import FullBox from '@/components/full-box';
import type { CallMember } from '@/contexts/chat/scripts/hooks/useChatMediaCall/types';
import { useColorAnalyzer } from '@/scripts/hooks/useColorAnalyzer';
import useDragMove from '../hooks/useDragMove';
import styles from './index.module.scss';
import { SortableGrid, SortableGridItem } from './components/sortableGrid';
import CallCard from './components/callCard';
import CallOperate from './components/callOperate';
import DirectionIcon from './images/direction.svg?react';

export interface CallMemberItem extends CallMember {
  fixed: boolean;
  id: string;
  screenSharing: boolean;
}

const MediaModal = () => {
  const {
    session: { user },
  } = useSessionContext();
  const { chatMediaCall, chatMediaQuickVO } = useChatContext();
  const { roomUsers } = chatMediaQuickVO;
  const { dragRef, hoverOverRef, hoverLeaveRef, direction, isMouseOver, style } =
    useDragMove({
      disabled: chatMediaCall.mediaModalZoomIn,
      marginTop: 100,
      thresholdTop: 200,
    });
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const [gridContainerInfo, setGridContainerInfo] = useState({ width: 0, height: 0 });
  const [gridCols, setGridCols] = useState<number>(0);
  const [callMembers, setCallMembers] = useState<CallMemberItem[]>([]);

  const isOnlyOne = useMemo(() => callMembers.length === 1, [callMembers]);

  const bgColor = useColorAnalyzer(chatMediaCall.chatMediaInfo?.chat?.user?.avatar, {
    format: 'rgb',
    samplingStep: 8,
    // alpha: 0.5,
  });

  const { fixedMembers, noFixedMembers, selfMember } = useMemo(() => {
    const fixed: CallMemberItem[] = [];
    const noFixed: CallMemberItem[] = [];
    const self: CallMemberItem[] = [];

    callMembers.forEach((m) => {
      if (m.fixed) {
        fixed.push(m);
      } else {
        if (m.id === user?.userId) {
          self.push(m);
        } else {
          noFixed.push(m);
        }
      }
    });
    return {
      fixedMembers: isOnlyOne ? [] : fixed,
      noFixedMembers: isOnlyOne ? self : noFixed,
      selfMember: isOnlyOne ? [] : self,
    };
  }, [callMembers, user, isOnlyOne]);

  const gridTemplateColumns = useMemo(
    () => `repeat(${chatMediaCall.mediaModalZoomIn ? gridCols : 1}, 1fr)`,
    [chatMediaCall.mediaModalZoomIn, gridCols]
  );

  const gridRows = useMemo(() => {
    if (chatMediaCall.mediaModalZoomIn && noFixedMembers.length > 2) {
      if (fixedMembers.length && noFixedMembers.length > 5) {
        return 3;
      }
      return 2;
    }
    return 1;
  }, [chatMediaCall.mediaModalZoomIn, noFixedMembers.length, fixedMembers.length]);

  const gridAutoRows = useMemo(
    () =>
      (gridContainerInfo.height -
        (chatMediaCall.mediaModalZoomIn && gridRows > 1 ? (gridRows - 1) * 10 : 0)) /
        gridRows +
      'px',
    [chatMediaCall.mediaModalZoomIn, gridContainerInfo.height, gridRows]
  );

  const getGridCols = useCallback((counts: number) => {
    if (counts <= 2) return 1;
    if (counts < 6) return 2;
    return 3;
  }, []);

  const getFixedGridCols = useCallback((counts: number) => (counts <= 3 ? 1 : 2), []);

  const handleFixed = useCallback(
    (item: any) => {
      setCallMembers((prevMembers) =>
        prevMembers.map((member) => ({
          ...member,
          fixed: item.id === member.id ? !member.fixed : false,
        }))
      );
      setGridCols(
        !item.fixed
          ? getFixedGridCols(callMembers.length)
          : getGridCols(callMembers.length)
      );
    },
    [callMembers.length, getGridCols, getFixedGridCols]
  );

  const handleDragEnd = useCallback((newItems: Record<string, any>[]) => {
    setCallMembers(
      (prev) =>
        [
          ...newItems,
          ...prev.filter((item) => !newItems.find((newItem) => newItem.id === item.id)),
        ] as CallMemberItem[]
    );
  }, []);

  useEffect(() => {
    setGridCols(getGridCols(callMembers.length));
  }, [callMembers.length, getGridCols]);

  useEffect(() => {
    if (!gridContainerRef.current) return;
    const observe = new ResizeObserver(([dom]) => {
      setGridContainerInfo({
        width: (dom.target as HTMLDivElement)?.offsetWidth,
        height: (dom.target as HTMLDivElement)?.offsetHeight,
      });
    });
    observe.observe(gridContainerRef.current);
    return () => observe.disconnect();
  }, [callMembers]);

  useEffect(() => {
    const data = (chatMediaCall.chatMediaInfo?.callMembers || [])?.flatMap((member) => {
      const roomUser = roomUsers?.find((roomUser) => roomUser.id === member.userId);
      const isScreenSharing = !!roomUser?.callActionMap?.screenSharing_video;

      const baseMember = {
        ...member,
        screenSharing: false,
        id: member.userId,
        fixed: false,
      };
      if (!isScreenSharing) return [baseMember];

      const screenMember = {
        ...member,
        screenSharing: true,
        id: `${member.userId}_ScreenSharing`,
        fixed: false,
      };

      return [screenMember, baseMember];
    });
    setCallMembers((prevCallMembers) => {
      const oldData = prevCallMembers.filter((member) =>
        data.find((item) => member.id === item.id)
      );
      const resData = uniqBy([...oldData, ...data], 'id');

      return resData
        .map((item, index) => {
          const curMemberIndex = prevCallMembers.findIndex(
            (member) => member.id === item.id
          );
          if (curMemberIndex > -1) {
            const prevMember = prevCallMembers[curMemberIndex];
            return {
              ...prevMember,
              ...item,
              fixed: prevMember.fixed,
              sortIndex: 0,
            };
          } else {
            return {
              ...item,
              sortIndex: item.screenSharing ? index : 0,
            };
          }
        })
        .sort((a, b) => b.sortIndex - a.sortIndex);
    });
  }, [chatMediaCall.chatMediaInfo?.callMembers, roomUsers]);

  useLayoutEffect(() => {
    if (!chatMediaCall.openMediaModal) {
      setCallMembers([]);
    }
  }, [chatMediaCall.openMediaModal]);

  const renderCallCards = (members: CallMemberItem[]) =>
    members.map((member) => (
      <CallCard
        key={member.id}
        mediaModalZoomIn={chatMediaCall.mediaModalZoomIn}
        user={user!}
        member={member}
        onFixed={() => handleFixed(member)}
      />
    ));

  return (
    <div
      id="mediaModal"
      ref={dragRef}
      style={style}
      className={cls(
        styles.mediaCallModal,
        chatMediaCall.openMediaModal ? styles.open : styles.close,
        !chatMediaCall.mediaModalZoomIn && styles.zoomIn
      )}
    >
      <FullBox
        ref={hoverLeaveRef}
        className={cls(
          styles.mediaCallModalWrapper,
          !chatMediaCall.mediaModalZoomIn && styles.zoomIn
        )}
        style={{
          background: chatMediaCall.mediaModalZoomIn ? '' : bgColor,
        }}
        column
        rearChildren={<CallOperate />}
      >
        <div
          className={cls(
            styles.mediaCallContainer,
            !chatMediaCall.mediaModalZoomIn && styles.zoomIn
          )}
        >
          <div className={cls(styles.left, !fixedMembers.length && styles.unLeft)}>
            {renderCallCards(fixedMembers)}
          </div>

          <div
            className={styles.right}
            hidden={!chatMediaCall.mediaModalZoomIn && !!fixedMembers.length}
            ref={gridContainerRef}
          >
            <SortableGrid onDragEnd={handleDragEnd} items={noFixedMembers}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns,
                  gridAutoRows,
                  gap: 10,
                }}
              >
                {noFixedMembers.map((member, index) => (
                  <SortableGridItem
                    id={member.id}
                    key={member.id}
                    disabled={
                      !chatMediaCall.mediaModalZoomIn || noFixedMembers.length === 1
                    }
                    hidden={!chatMediaCall.mediaModalZoomIn && index !== 0}
                  >
                    <CallCard
                      key={member.id}
                      mediaModalZoomIn={chatMediaCall.mediaModalZoomIn}
                      user={user!}
                      member={member}
                      onFixed={() => handleFixed(member)}
                      isOnlyOne={isOnlyOne}
                    />
                  </SortableGridItem>
                ))}
              </div>
            </SortableGrid>
          </div>
          {renderCallCards(selfMember)}
        </div>
      </FullBox>
      <div
        ref={hoverOverRef}
        className={cls(styles.mediaCallLabel, {
          [styles.left]: direction.isLeft && isMouseOver,
          [styles.right]: direction.isRight && isMouseOver,
        })}
      >
        <DirectionIcon />
      </div>
    </div>
  );
};

export default MediaModal;
